import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { countWeekdays, localDateStr } from '../utils/date'
import useIsMobile from '../hooks/useIsMobile'

export default function Leave({ profile }) {
  const isAdmin = profile.role === 'admin'
  const [employees, setEmployees] = useState([])
  const [leaveList, setLeaveList] = useState([])
  const [filter, setFilter] = useState('all')
  const [showModal, setShowModal] = useState(false)
  const [showQuotaModal, setShowQuotaModal] = useState(false)
  const [form, setForm] = useState({ type:'연차', start:'', end:'', reason:'' })
  const [loading, setLoading] = useState(false)
  const [view, setView] = useState('list') // 'list' | 'calendar'
  const [calMonth, setCalMonth] = useState(() => localDateStr().slice(0, 7))
  const isMobile = useIsMobile()

  useEffect(() => {
    fetchEmployees()
    fetchLeave()
  }, [])

  async function fetchEmployees() {
    const { data } = await supabase.from('profiles').select('*').neq('role', 'admin')
    if (data) setEmployees(data)
  }

  async function fetchLeave() {
    let query = supabase.from('leave_requests').select('*').order('created_at', { ascending: false })
    if (!isAdmin) query = query.eq('emp_id', profile.id)
    const { data } = await query
    if (data) setLeaveList(data)
  }

  async function submitLeave() {
    if (!form.start || !form.end) { alert('날짜를 입력해주세요'); return }
    if (form.end < form.start) { alert('종료일이 시작일보다 빠릅니다'); return }
    // 주말(토·일) 제외 일수 — 전부 주말이면 1일 처리하지 않고 막음
    const weekdays = countWeekdays(form.start, form.end)
    if (!form.type.includes('반차') && weekdays === 0) {
      alert('선택한 기간이 모두 주말입니다. 날짜를 확인해주세요.')
      return
    }
    setLoading(true)
    const days = form.type.includes('반차') ? 0.5 : weekdays
    await supabase.from('leave_requests').insert({
      emp_id: profile.id,
      emp_name: profile.name,
      type: form.type,
      start_date: form.start,
      end_date: form.end,
      days,
      reason: form.reason || '(없음)',
      status: 'pending'
    })
    setShowModal(false)
    setForm({ type:'연차', start:'', end:'', reason:'' })
    fetchLeave()
    setLoading(false)
  }

  async function approveLeave(id, empId, days, ok) {
    await supabase.from('leave_requests').update({ status: ok ? 'approved' : 'rejected' }).eq('id', id)
    if (ok) {
      const emp = employees.find(e => e.id === empId)
      if (emp) await supabase.from('profiles').update({ leave_used: emp.leave_used + days }).eq('id', empId)
    }
    fetchEmployees()
    fetchLeave()
  }

  async function saveQuota(empId, quota, used) {
    await supabase.from('profiles').update({ leave_quota: quota, leave_used: used }).eq('id', empId)
    fetchEmployees()
  }

  async function deleteLeave(id) {
    const l = leaveList.find(x => x.id === id)
    if (!l) return
    if (!isAdmin) {
      if (l.emp_id !== profile.id) {
        alert('본인이 신청한 건만 삭제할 수 있습니다.')
        return
      }
      if (l.status !== 'pending') {
        alert('대기 중인 신청만 삭제할 수 있습니다.')
        return
      }
    }
    if (!window.confirm('이 연차 신청을 삭제할까요?')) return

    if (isAdmin && l.status === 'approved') {
      const { data: emp } = await supabase.from('profiles').select('leave_used').eq('id', l.emp_id).single()
      if (emp) {
        const next = Math.max(0, (emp.leave_used || 0) - (Number(l.days) || 0))
        await supabase.from('profiles').update({ leave_used: next }).eq('id', l.emp_id)
      }
    }

    const { error } = await supabase.from('leave_requests').delete().eq('id', id)
    if (error) {
      alert('삭제에 실패했습니다.\n' + error.message + '\n\nSupabase에서 leave_requests DELETE 권한(RLS)을 확인해 주세요.')
      return
    }
    fetchLeave()
    fetchEmployees()
  }

  const filtered = leaveList.filter(l => filter === 'all' || l.status === filter)
  const tableColSpanAdmin = 8
  const tableColSpanEmp = 7

  const statusLabel = s => ({ pending:'대기중', approved:'승인', rejected:'반려' }[s] || s)
  const statusColor = s => ({ pending:'#c0601a', approved:'#2d5a3d', rejected:'#b83030' }[s])
  const statusBg = s => ({ pending:'#fff3e0', approved:'#e8f2eb', rejected:'#fdeaea' }[s])

  return (
    <div>
      {/* 헤더 */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:700, marginBottom:4 }}>연차 관리</div>
          <div style={{ fontSize:13, color:'#a09e99' }}>
            {isAdmin ? '전체 직원 연차 현황 및 승인/반려 관리' : '내 연차 현황 및 신청'}
          </div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {isAdmin && (
            <button onClick={() => setShowQuotaModal(true)}
              style={{ padding:'8px 14px', borderRadius:8, border:'1px solid #e5e3de', background:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
              ✏️ 연차 부여
            </button>
          )}
          <button onClick={() => setShowModal(true)}
            style={{ padding:'8px 16px', borderRadius:8, border:'none', background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
            + 연차 신청
          </button>
        </div>
      </div>

      {/* 관리자: 직원별 현황 */}
      {isAdmin && (
        <div style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:10, marginBottom:20 }}>
          <div style={{ padding:'16px 20px', borderBottom:'1px solid #e5e3de', fontWeight:700, fontSize:14 }}>직원별 연차 현황</div>
          {isMobile ? (
            <div style={{ padding:'4px 0' }}>
              {employees.map(e => {
                const remain = e.leave_quota - e.leave_used
                const pct = Math.round(e.leave_used / e.leave_quota * 100) || 0
                return (
                  <div key={e.id} style={{ padding:'12px 16px', borderBottom:'1px solid #f0eeea' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                      <span style={{ fontSize:13, fontWeight:600 }}>
                        {e.name} <span style={{ fontSize:11, fontWeight:400, color:'#a09e99' }}>{e.dept}</span>
                      </span>
                      <span style={{ fontSize:12 }}>
                        잔여 <b style={{ color: remain <= 3 ? '#b83030' : remain <= 7 ? '#c0601a' : '#2d5a3d' }}>{remain}일</b>
                        <span style={{ color:'#a09e99' }}> / {e.leave_quota}일</span>
                      </span>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <div style={{ flex:1, background:'#e5e3de', borderRadius:4, height:6, overflow:'hidden' }}>
                        <div style={{ width:pct+'%', height:'100%', background:'#4a8c60', borderRadius:4 }} />
                      </div>
                      <span style={{ fontSize:10, color:'#a09e99', flexShrink:0 }}>{pct}%</span>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr>
                {['직원','부서','총 연차','사용','잔여','사용률'].map(h => (
                  <th key={h} style={{ padding:'10px 14px', fontSize:11, fontWeight:600, color:'#a09e99', borderBottom:'1px solid #e5e3de', background:'#f9f8f6', textAlign:'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {employees.map(e => {
                const remain = e.leave_quota - e.leave_used
                const pct = Math.round(e.leave_used / e.leave_quota * 100)
                return (
                  <tr key={e.id}>
                    <td style={{ padding:'12px 14px', fontWeight:600 }}>{e.name}</td>
                    <td style={{ padding:'12px 14px', color:'#6b6860' }}>{e.dept}</td>
                    <td style={{ padding:'12px 14px' }}>{e.leave_quota}일</td>
                    <td style={{ padding:'12px 14px' }}>{e.leave_used}일</td>
                    <td style={{ padding:'12px 14px', fontWeight:700, color: remain <= 3 ? '#b83030' : remain <= 7 ? '#c0601a' : '#2d5a3d' }}>{remain}일</td>
                    <td style={{ padding:'12px 14px', minWidth:140 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ flex:1, background:'#e5e3de', borderRadius:4, height:6, overflow:'hidden' }}>
                          <div style={{ width:pct+'%', height:'100%', background:'#4a8c60', borderRadius:4 }} />
                        </div>
                        <span style={{ fontSize:11, color:'#a09e99' }}>{pct}%</span>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          )}
        </div>
      )}

      {/* 직원: 내 연차 현황 */}
      {!isAdmin && (() => {
        const me = employees.find(e => e.id === profile.id) || profile
        const remain = (me.leave_quota || 15) - (me.leave_used || 0)
        const pct = Math.round((me.leave_used || 0) / (me.leave_quota || 15) * 100)
        return (
          <div style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:10, padding:24, marginBottom:20 }}>
            <div style={{ fontWeight:700, fontSize:13, color:'#6b6860', marginBottom:14 }}>내 연차 현황</div>
            <div style={{ display:'flex', alignItems:'center', gap:20 }}>
              <div>
                <div style={{ fontSize:40, fontWeight:700, color:'#2d5a3d', lineHeight:1 }}>{remain}</div>
                <div style={{ fontSize:12, color:'#a09e99', marginTop:2 }}>잔여 일수</div>
              </div>
              <div style={{ flex:1 }}>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#a09e99', marginBottom:6 }}>
                  <span>사용 <b style={{ color:'#6b6860' }}>{me.leave_used || 0}</b>일</span>
                  <span>총 <b style={{ color:'#6b6860' }}>{me.leave_quota || 15}</b>일</span>
                </div>
                <div style={{ background:'#e5e3de', borderRadius:4, height:8, overflow:'hidden' }}>
                  <div style={{ width:pct+'%', height:'100%', background:'#4a8c60', borderRadius:4 }} />
                </div>
              </div>
            </div>
          </div>
        )
      })()}

      {/* 신청 목록 */}
      <div style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:10 }}>
        <div style={{ padding:'16px 20px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          <span style={{ fontWeight:700, fontSize:14 }}>{isAdmin ? '전체 연차 신청 목록' : '내 연차 신청 내역'}</span>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <div style={{ display:'flex', border:'1px solid #e5e3de', borderRadius:8, overflow:'hidden' }}>
              {[['list','📋 리스트'], ['calendar','📅 캘린더']].map(([v, label]) => (
                <button key={v} type="button" onClick={() => setView(v)}
                  style={{
                    padding:'7px 12px', border:'none', fontSize:12, fontFamily:'inherit', cursor:'pointer', fontWeight:600,
                    background: view === v ? '#2d5a3d' : '#fff',
                    color: view === v ? '#fff' : '#6b6860',
                  }}>
                  {label}
                </button>
              ))}
            </div>
            {isAdmin && view === 'list' && (
              <select value={filter} onChange={e => setFilter(e.target.value)}
                style={{ padding:'7px 10px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', cursor:'pointer' }}>
                <option value="all">전체</option>
                <option value="pending">대기중</option>
                <option value="approved">승인</option>
                <option value="rejected">반려</option>
              </select>
            )}
          </div>
        </div>
        {view === 'calendar' ? (
          <LeaveCalendar leaves={leaveList} month={calMonth} onMonthChange={setCalMonth} isAdmin={isAdmin} />
        ) : isMobile ? (
          /* 모바일: 카드 목록 */
          filtered.length === 0 ? (
            <div style={{ textAlign:'center', padding:40, color:'#a09e99', fontSize:13 }}>신청 내역이 없습니다</div>
          ) : (
            <div style={{ padding:'4px 0' }}>
              {filtered.map(l => (
                <div key={l.id} style={{ padding:'14px 16px', borderBottom:'1px solid #f0eeea' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6, gap:8 }}>
                    <span style={{ fontSize:13, fontWeight:600, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {isAdmin ? `${l.emp_name} · ${l.type}` : l.type}
                    </span>
                    <span style={{ background:statusBg(l.status), color:statusColor(l.status), padding:'3px 9px', borderRadius:20, fontSize:11, fontWeight:500, flexShrink:0 }}>
                      {statusLabel(l.status)}
                    </span>
                  </div>
                  <div style={{ fontSize:12, color:'#6b6860', marginBottom:2 }}>
                    {l.start_date} ~ {l.end_date} · <b>{l.days}일</b>
                  </div>
                  {l.reason && l.reason !== '(없음)' && (
                    <div style={{ fontSize:12, color:'#a09e99', marginBottom:6, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l.reason}</div>
                  )}
                  {(isAdmin || l.status === 'pending') && (
                    <div style={{ display:'flex', gap:6, marginTop:8 }}>
                      {isAdmin && l.status === 'pending' && (
                        <>
                          <button type="button" onClick={() => approveLeave(l.id, l.emp_id, l.days, true)}
                            style={{ flex:1, padding:'7px 0', background:'#e8f2eb', color:'#2d5a3d', border:'none', borderRadius:6, cursor:'pointer', fontSize:12, fontWeight:600 }}>승인</button>
                          <button type="button" onClick={() => approveLeave(l.id, l.emp_id, l.days, false)}
                            style={{ flex:1, padding:'7px 0', background:'#fdeaea', color:'#b83030', border:'none', borderRadius:6, cursor:'pointer', fontSize:12, fontWeight:600 }}>반려</button>
                        </>
                      )}
                      <button type="button" onClick={() => deleteLeave(l.id)}
                        style={{ flex: isAdmin && l.status === 'pending' ? 'none' : 1, padding:'7px 14px', background:'#f9f8f6', color:'#b83030', border:'1px solid #e5e3de', borderRadius:6, cursor:'pointer', fontSize:12 }}>삭제</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        ) : isAdmin ? (
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr>
                {['직원','유형','시작일','종료일','일수','사유','상태','액션'].map(h => (
                  <th key={h} style={{ padding:'10px 14px', fontSize:11, fontWeight:600, color:'#a09e99', borderBottom:'1px solid #e5e3de', background:'#f9f8f6', textAlign:'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={tableColSpanAdmin} style={{ textAlign:'center', padding:40, color:'#a09e99' }}>신청 내역이 없습니다</td></tr>
              ) : filtered.map(l => (
                <tr key={l.id} style={{ borderBottom:'1px solid #e5e3de' }}>
                  <td style={{ padding:'12px 14px', fontWeight:600 }}>{l.emp_name}</td>
                  <td style={{ padding:'12px 14px' }}>{l.type}</td>
                  <td style={{ padding:'12px 14px' }}>{l.start_date}</td>
                  <td style={{ padding:'12px 14px' }}>{l.end_date}</td>
                  <td style={{ padding:'12px 14px' }}>{l.days}일</td>
                  <td style={{ padding:'12px 14px', color:'#6b6860', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l.reason}</td>
                  <td style={{ padding:'12px 14px' }}>
                    <span style={{ background:statusBg(l.status), color:statusColor(l.status), padding:'3px 9px', borderRadius:20, fontSize:11, fontWeight:500 }}>
                      {statusLabel(l.status)}
                    </span>
                  </td>
                  <td style={{ padding:'12px 14px' }}>
                    <div style={{ display:'flex', gap:6 }}>
                      {l.status === 'pending' && (
                        <>
                          <button type="button" onClick={() => approveLeave(l.id, l.emp_id, l.days, true)}
                            style={{ padding:'4px 10px', background:'#e8f2eb', color:'#2d5a3d', border:'none', borderRadius:6, cursor:'pointer', fontSize:12 }}>승인</button>
                          <button type="button" onClick={() => approveLeave(l.id, l.emp_id, l.days, false)}
                            style={{ padding:'4px 10px', background:'#fdeaea', color:'#b83030', border:'none', borderRadius:6, cursor:'pointer', fontSize:12 }}>반려</button>
                        </>
                      )}
                      <button type="button" onClick={() => deleteLeave(l.id)}
                        style={{ padding:'4px 10px', background:'#fdeaea', color:'#b83030', border:'none', borderRadius:6, cursor:'pointer', fontSize:12 }}>🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr>
                {['유형','시작일','종료일','일수','사유','상태',''].map((h, i) => (
                  <th key={i} style={{ padding:'10px 14px', fontSize:11, fontWeight:600, color:'#a09e99', borderBottom:'1px solid #e5e3de', background:'#f9f8f6', textAlign:'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={tableColSpanEmp} style={{ textAlign:'center', padding:40, color:'#a09e99' }}>신청 내역이 없습니다</td></tr>
              ) : filtered.map(l => (
                <tr key={l.id} style={{ borderBottom:'1px solid #e5e3de' }}>
                  <td style={{ padding:'12px 14px' }}>{l.type}</td>
                  <td style={{ padding:'12px 14px' }}>{l.start_date}</td>
                  <td style={{ padding:'12px 14px' }}>{l.end_date}</td>
                  <td style={{ padding:'12px 14px' }}>{l.days}일</td>
                  <td style={{ padding:'12px 14px', color:'#6b6860' }}>{l.reason}</td>
                  <td style={{ padding:'12px 14px' }}>
                    <span style={{ background:statusBg(l.status), color:statusColor(l.status), padding:'3px 9px', borderRadius:20, fontSize:11, fontWeight:500 }}>
                      {statusLabel(l.status)}
                    </span>
                  </td>
                  <td style={{ padding:'12px 14px' }}>
                    {l.status === 'pending' && (
                      <button type="button" onClick={() => deleteLeave(l.id)}
                        style={{ padding:'4px 10px', background:'#fdeaea', color:'#b83030', border:'none', borderRadius:6, cursor:'pointer', fontSize:12 }}>
                        삭제
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 연차 신청 모달 */}
      {showModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div style={{ background:'#fff', borderRadius:14, width:480, maxWidth:'95vw', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:15, fontWeight:700 }}>연차 신청</span>
              <button onClick={() => setShowModal(false)} style={{ border:'none', background:'#f9f8f6', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:16 }}>✕</button>
            </div>
            <div style={{ padding:'20px 24px' }}>
              <div style={{ marginBottom:14 }}>
                <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>유형</label>
                <select value={form.type} onChange={e => setForm({...form, type:e.target.value})}
                  style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit' }}>
                  {['연차','반차(오전)','반차(오후)','병가','경조사'].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>시작일</label>
                  <input type="date" value={form.start} onChange={e => setForm({...form, start:e.target.value})}
                    style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }} />
                </div>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>종료일</label>
                  <input type="date" value={form.end} onChange={e => setForm({...form, end:e.target.value})}
                    style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }} />
                </div>
              </div>
              {form.start && form.end && form.end >= form.start && (
                <div style={{ background:'#f9f8f6', border:'1px solid #e5e3de', borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:13 }}>
                  차감 일수: <b style={{ color:'#2d5a3d' }}>
                    {form.type.includes('반차') ? 0.5 : countWeekdays(form.start, form.end)}일
                  </b>
                  <span style={{ fontSize:11, color:'#a09e99', marginLeft:8 }}>(주말 제외)</span>
                </div>
              )}
              <div>
                <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>사유</label>
                <textarea value={form.reason} onChange={e => setForm({...form, reason:e.target.value})}
                  placeholder="신청 사유" rows={3}
                  style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', resize:'vertical', boxSizing:'border-box' }} />
              </div>
            </div>
            <div style={{ padding:'16px 24px', borderTop:'1px solid #e5e3de', display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => setShowModal(false)}
                style={{ padding:'8px 16px', border:'1px solid #e5e3de', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13 }}>취소</button>
              <button onClick={submitLeave} disabled={loading}
                style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
                {loading ? '신청 중...' : '신청하기'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 연차 부여 모달 */}
      {showQuotaModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => e.target === e.currentTarget && setShowQuotaModal(false)}>
          <div style={{ background:'#fff', borderRadius:14, width:540, maxWidth:'95vw', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:15, fontWeight:700 }}>연차 부여 / 수정</span>
              <button onClick={() => setShowQuotaModal(false)} style={{ border:'none', background:'#f9f8f6', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:16 }}>✕</button>
            </div>
            <div style={{ padding:'20px 24px' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr>
                    {['직원','부서','총 연차','사용일수','잔여'].map(h => (
                      <th key={h} style={{ padding:'8px 12px', fontSize:11, fontWeight:600, color:'#a09e99', borderBottom:'1px solid #e5e3de', background:'#f9f8f6', textAlign: h==='총 연차'||h==='사용일수'||h==='잔여' ? 'center' : 'left' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {employees.map(e => (
                    <QuotaRow key={e.id} emp={e} onSave={saveQuota} />
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding:'16px 24px', borderTop:'1px solid #e5e3de', display:'flex', justifyContent:'flex-end' }}>
              <button onClick={() => setShowQuotaModal(false)}
                style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>닫기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function LeaveCalendar({ leaves, month, onMonthChange, isAdmin }) {
  const [y, m] = month.split('-').map(Number)
  const totalDays = new Date(y, m, 0).getDate()
  const firstDow = new Date(y, m - 1, 1).getDay()
  const todayStr = localDateStr()

  function moveMonth(delta) {
    const d = new Date(y, m - 1 + delta, 1)
    onMonthChange(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
  }

  // 승인 + 대기중만 표시 (반려 제외)
  const visible = leaves.filter(l => l.status === 'approved' || l.status === 'pending')

  function leavesOn(dateStr) {
    return visible.filter(l => l.start_date <= dateStr && dateStr <= l.end_date)
  }

  // 앞 빈칸 + 날짜 셀
  const cells = [
    ...Array.from({ length: firstDow }, () => null),
    ...Array.from({ length: totalDays }, (_, i) => i + 1),
  ]

  return (
    <div style={{ padding:16 }}>
      {/* 월 이동 */}
      <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:14, marginBottom:14 }}>
        <button type="button" onClick={() => moveMonth(-1)}
          style={{ border:'1px solid #e5e3de', background:'#fff', borderRadius:8, width:30, height:30, cursor:'pointer', fontSize:14 }}>‹</button>
        <span style={{ fontSize:15, fontWeight:700 }}>{y}년 {m}월</span>
        <button type="button" onClick={() => moveMonth(1)}
          style={{ border:'1px solid #e5e3de', background:'#fff', borderRadius:8, width:30, height:30, cursor:'pointer', fontSize:14 }}>›</button>
      </div>

      {/* 범례 */}
      <div style={{ display:'flex', gap:10, justifyContent:'center', marginBottom:12, fontSize:11, color:'#6b6860' }}>
        <span><span style={{ display:'inline-block', width:10, height:10, background:'#e8f2eb', border:'1px solid #2d5a3d', borderRadius:3, marginRight:4, verticalAlign:'middle' }} />승인</span>
        <span><span style={{ display:'inline-block', width:10, height:10, background:'#fff3e0', border:'1px dashed #c0601a', borderRadius:3, marginRight:4, verticalAlign:'middle' }} />대기중</span>
      </div>

      {/* 요일 헤더 */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:4, marginBottom:4 }}>
        {['일','월','화','수','목','금','토'].map((d, i) => (
          <div key={d} style={{ textAlign:'center', fontSize:11, fontWeight:600, padding:'4px 0',
            color: i === 0 ? '#b83030' : i === 6 ? '#1e4a8c' : '#6b6860' }}>{d}</div>
        ))}
      </div>

      {/* 날짜 그리드 */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:4 }}>
        {cells.map((day, idx) => {
          if (day === null) return <div key={'e' + idx} />
          const dateStr = `${month}-${String(day).padStart(2, '0')}`
          const dow = (firstDow + day - 1) % 7
          const dayLeaves = leavesOn(dateStr)
          const isToday = dateStr === todayStr
          const shown = dayLeaves.slice(0, 3)
          return (
            <div key={dateStr} style={{
              minHeight:72, border:'1px solid ' + (isToday ? '#4a8c60' : '#f0eeea'), borderRadius:8,
              padding:'4px 5px', background: dow === 0 || dow === 6 ? '#f9f8f6' : '#fff',
              boxShadow: isToday ? '0 0 0 1px #4a8c60' : 'none',
            }}>
              <div style={{ fontSize:11, fontWeight: isToday ? 700 : 500, marginBottom:3,
                color: dow === 0 ? '#b83030' : dow === 6 ? '#1e4a8c' : isToday ? '#2d5a3d' : '#6b6860' }}>
                {day}
              </div>
              {shown.map(l => (
                <div key={l.id} title={`${l.emp_name} · ${l.type} (${l.start_date}~${l.end_date})${l.status === 'pending' ? ' · 대기중' : ''}`}
                  style={{
                    fontSize:10, padding:'2px 4px', borderRadius:4, marginBottom:2,
                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontWeight:600,
                    background: l.status === 'pending' ? '#fff3e0' : '#e8f2eb',
                    color: l.status === 'pending' ? '#c0601a' : '#2d5a3d',
                    border: l.status === 'pending' ? '1px dashed #c0601a' : '1px solid transparent',
                  }}>
                  {isAdmin ? l.emp_name : l.type}
                </div>
              ))}
              {dayLeaves.length > 3 && (
                <div style={{ fontSize:9, color:'#a09e99' }}>+{dayLeaves.length - 3}명</div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function QuotaRow({ emp, onSave }) {
  const [quota, setQuota] = useState(emp.leave_quota)
  const [used, setUsed] = useState(emp.leave_used)
  const remain = quota - used

  async function handleSave() {
    await onSave(emp.id, Number(quota), Number(used))
    alert(`${emp.name} 연차 저장 완료!`)
  }

  return (
    <tr style={{ borderBottom:'1px solid #e5e3de' }}>
      <td style={{ padding:'10px 12px', fontWeight:600 }}>{emp.name}</td>
      <td style={{ padding:'10px 12px', fontSize:12, color:'#a09e99' }}>{emp.dept}</td>
      <td style={{ padding:'8px 12px', textAlign:'center' }}>
        <input type="number" value={quota} onChange={e => setQuota(e.target.value)} min={0}
          style={{ width:60, padding:'6px 8px', border:'1px solid #e5e3de', borderRadius:6, fontSize:13, textAlign:'center', fontFamily:'inherit' }} />
      </td>
      <td style={{ padding:'8px 12px', textAlign:'center' }}>
        <input type="number" value={used} onChange={e => setUsed(e.target.value)} min={0}
          style={{ width:60, padding:'6px 8px', border:'1px solid #e5e3de', borderRadius:6, fontSize:13, textAlign:'center', fontFamily:'inherit' }} />
      </td>
      <td style={{ padding:'10px 12px', textAlign:'center' }}>
        <span style={{ fontWeight:700, color: remain<=3?'#b83030':remain<=7?'#c0601a':'#2d5a3d', marginRight:8 }}>{remain}일</span>
        <button onClick={handleSave}
          style={{ padding:'3px 10px', background:'#e8f2eb', color:'#2d5a3d', border:'none', borderRadius:6, cursor:'pointer', fontSize:11, fontWeight:600 }}>저장</button>
      </td>
    </tr>
  )
}